import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { AppShell } from "@/components/AppShell";
import { PremiumLock } from "@/components/PremiumLock";
import { findLender, recommendationMeta, lenders, type Lender } from "@/lib/mock-data";
import { ScoreCard } from "@/components/ScoreCard";
import { AlertTriangle, CheckCircle2, Building2, MapPin, Calendar, ArrowLeft, MessageSquare } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

export const Route = createFileRoute("/lenders/$slug")({
  head: ({ params }) => {
    const l = findLender(params.slug);
    const title = l ? `${l.name} Review — LendyTrack` : "Lender — LendyTrack";
    const desc = l?.summary ?? "Lender risk profile and review on LendyTrack.";
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `/lenders/${params.slug}` },
      ],
      links: [{ rel: "canonical", href: `/lenders/${params.slug}` }],
    };
  },
  loader: ({ params }) => {
    const l = findLender(params.slug);
    if (!l) throw notFound();
    return l as NonNullable<ReturnType<typeof findLender>>;
  },
  notFoundComponent: () => (
    <AppShell>
      <div className="rounded-xl border border-border bg-card p-10 text-center">
        <h2 className="font-display text-2xl">Lender not found</h2>
        <Link to="/lenders" className="mt-4 inline-block text-primary hover:underline">Back to search</Link>
      </div>
    </AppShell>
  ),
  errorComponent: () => (
    <AppShell>
      <div className="rounded-xl border border-border bg-card p-10 text-center">Couldn't load lender.</div>
    </AppShell>
  ),
  component: LenderProfile,
});

function LenderProfile() {
  const l = Route.useLoaderData() as Lender;
  const meta = recommendationMeta[l.recommendation];
  const alternatives = lenders.filter((x) => x.recommendation === "safe" && x.slug !== l.slug).slice(0, 3);

  return (
    <AppShell>
      <Link to="/lenders" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-3 w-3" /> Back to search
      </Link>

      <div className={`mt-4 rounded-2xl border p-6 shadow-soft ${meta.bg}`}>
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="grid h-12 w-12 place-items-center rounded-xl bg-card text-primary shadow-soft">
                <Building2 className="h-6 w-6" />
              </div>
              <div>
                <h1 className="font-display text-3xl font-semibold tracking-tight">{l.name}</h1>
                <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {l.hq}</span>
                  <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> Founded {l.founded}</span>
                  <span>· {l.category}</span>
                </div>
              </div>
            </div>
            <p className="mt-4 max-w-2xl text-sm text-muted-foreground">{l.summary}</p>
          </div>
          <div className="text-center">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Recommendation</div>
            <div className={`mt-1 font-display text-2xl font-semibold ${meta.tone}`}>{meta.label}</div>
          </div>
        </div>
      </div>

      {/* Scores */}
      <div className="mt-6 grid gap-4 md:grid-cols-3 lg:grid-cols-5">
        <ScoreCard label="Trust" score={l.scores.trust} />
        <ScoreCard label="Predatory Risk" score={l.scores.predatory} invert caption="Lower is safer" />
        <ScoreCard label="Sentiment" score={l.scores.sentiment} />
        <ScoreCard label="Complaints" score={l.scores.complaints} invert caption="Lower is better" />
        <ScoreCard label="Fee Transparency" score={l.scores.feeTransparency} />
      </div>

      {/* Loan terms */}
      <div className="mt-6 grid gap-4 md:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-5 shadow-soft">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">APR Range</div>
          <div className="mt-1 font-display text-2xl font-semibold">{l.aprRange[0]}% – {l.aprRange[1]}%</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-5 shadow-soft">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Loan amounts</div>
          <div className="mt-1 font-display text-2xl font-semibold">${l.amountRange[0].toLocaleString()} – ${l.amountRange[1].toLocaleString()}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-5 shadow-soft">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Term</div>
          <div className="mt-1 font-display text-2xl font-semibold">{l.termRange}</div>
        </div>
      </div>

      {/* Red / green flags */}
      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-danger/30 bg-danger/5 p-5">
          <div className="flex items-center gap-2 text-danger">
            <AlertTriangle className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-widest">Red flags</span>
          </div>
          <ul className="mt-3 space-y-2 text-sm">
            {l.redFlags.length === 0 && <li className="text-muted-foreground">No major red flags detected.</li>}
            {l.redFlags.map((f) => (
              <li key={f} className="flex gap-2"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-danger" />{f}</li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border border-success/30 bg-success/5 p-5">
          <div className="flex items-center gap-2 text-success">
            <CheckCircle2 className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-widest">Positive signals</span>
          </div>
          <ul className="mt-3 space-y-2 text-sm">
            {l.greenFlags.length === 0 && <li className="text-muted-foreground">No notable positive signals found.</li>}
            {l.greenFlags.map((f) => (
              <li key={f} className="flex gap-2"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-success" />{f}</li>
            ))}
          </ul>
        </div>
      </div>

      <PremiumLock
        title="Deep-dive: charts, AI summary & borrower reviews"
        description="Premium unlocks sentiment trends, complaint breakdowns, the AI risk summary, and real borrower reviews for every lender."
      >
      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-5 shadow-soft">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Sentiment trend</div>
              <div className="mt-0.5 font-display text-lg font-semibold">Last 6 months</div>
            </div>
          </div>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={l.sentimentTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.91 0.012 250)" />
                <XAxis dataKey="month" stroke="oklch(0.5 0.03 250)" fontSize={11} />
                <YAxis stroke="oklch(0.5 0.03 250)" fontSize={11} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid oklch(0.91 0.012 250)" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="positive" stroke="oklch(0.68 0.16 160)" strokeWidth={2.5} dot={false} />
                <Line type="monotone" dataKey="negative" stroke="oklch(0.6 0.22 25)" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-5 shadow-soft">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Complaint themes</div>
            <div className="mt-0.5 font-display text-lg font-semibold">Top categories</div>
          </div>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={l.complaintBreakdown} layout="vertical" margin={{ left: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.91 0.012 250)" />
                <XAxis type="number" stroke="oklch(0.5 0.03 250)" fontSize={11} />
                <YAxis dataKey="category" type="category" stroke="oklch(0.5 0.03 250)" fontSize={11} width={130} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid oklch(0.91 0.012 250)" }} />
                <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                  {l.complaintBreakdown.map((_, i) => (
                    <Cell key={i} fill={i % 2 === 0 ? "oklch(0.24 0.06 255)" : "oklch(0.6 0.22 25)"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      </PremiumLock>


      {/* AI summary */}
      <div className="mt-6 rounded-xl border border-border bg-gradient-hero p-6 text-primary-foreground shadow-soft">
        <div className="text-xs uppercase tracking-widest text-accent">AI Summary</div>
        <p className="mt-2 max-w-3xl text-sm text-primary-foreground/85">
          Based on {l.reviewCount.toLocaleString()} reviews and {l.complaintBreakdown.reduce((a, b) => a + b.count, 0).toLocaleString()} complaints,
          {l.recommendation === "avoid" || l.recommendation === "high-risk"
            ? ` ${l.name} shows strong predatory patterns. The most common borrower complaint is "${l.complaintBreakdown[0]?.category ?? "fees"}". `
            : ` ${l.name} demonstrates above-average transparency. Borrower complaints are primarily about "${l.complaintBreakdown[0]?.category ?? "fees"}". `}
          We recommend you {meta.label.toLowerCase()}.
        </p>
      </div>

      {/* Sample reviews */}
      {l.sampleReviews.length > 0 && (
        <div className="mt-6">
          <h2 className="font-display text-lg font-semibold">What borrowers say</h2>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            {l.sampleReviews.map((r) => (
              <div key={r.author + r.date} className="rounded-xl border border-border bg-card p-4 shadow-soft">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span className="font-semibold text-foreground">{r.author}</span>
                  <span>{r.date}</span>
                </div>
                <div className="mt-1 text-xs text-warning-foreground">{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</div>
                <p className="mt-2 text-sm text-muted-foreground">"{r.text}"</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Alternatives */}
      {l.recommendation !== "safe" && (
        <div className="mt-8 rounded-2xl border border-success/30 bg-success/5 p-6">
          <div className="flex items-center gap-2 text-success">
            <CheckCircle2 className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-widest">Safer alternatives</span>
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            {alternatives.map((a) => (
              <Link key={a.slug} to="/lenders/$slug" params={{ slug: a.slug }} className="rounded-xl border border-border bg-card p-4 hover:shadow-elevated">
                <div className="font-display text-base font-semibold">{a.name}</div>
                <div className="text-xs text-muted-foreground">APR {a.aprRange[0]}–{a.aprRange[1]}%</div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <Link
        to="/coach"
        className="mt-6 flex items-center justify-between rounded-xl border border-border bg-card p-5 shadow-soft hover:shadow-elevated"
      >
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-lg bg-accent/15 text-accent">
            <MessageSquare className="h-5 w-5" />
          </div>
          <div>
            <div className="font-display text-base font-semibold">Ask the AI Coach about {l.name}</div>
            <div className="text-xs text-muted-foreground">Get plain-English answers about risks, fees, and alternatives.</div>
          </div>
        </div>
        <span className="text-sm font-medium text-primary">Open chat →</span>
      </Link>
    </AppShell>
  );
}
